// F-V: "Generate Narration Audio" plan step. Fetches the read-only plan
// (every slot's status + billable characters + this month's budget) and lets
// staff untick languages before anything is spent. Only MISSING slots are
// generated; current / custom / no-description / outdated are listed as
// skipped (outdated audio is regenerated one exhibit at a time — KL
// 2026-10-05). Generate hands the slots to the app-level runner.

import { useMemo, useState, type FC } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@shared/components/ui/dialog";
import {
  planNarration,
  type NarrationKind,
  type NarrationPlan,
} from "@shared/api/narration";
import { useNarrationRun } from "./NarrationRunContext";
import {
  LANG_LABEL,
  NARRATION_LANGS,
  type NarrationLang,
} from "./NarrationBadges";

const fmt = (n: number) => n.toLocaleString("en-US");
const plural = (n: number) => (n === 1 ? "" : "s");

export interface NarrationSummary {
  toGenerate: NarrationPlan["items"];
  perLang: Record<NarrationLang, number>;
  current: number;
  custom: number;
  none: number;
  stale: number;
  chars: number;
}

/** Aggregate the plan for the ticked languages (pure; unit-tested). */
export function summarizePlan(
  plan: NarrationPlan,
  langs: NarrationLang[],
): NarrationSummary {
  const out: NarrationSummary = {
    toGenerate: [],
    perLang: { tc: 0, en: 0, sc: 0 },
    current: 0,
    custom: 0,
    none: 0,
    stale: 0,
    chars: 0,
  };
  for (const item of plan.items) {
    const lang = item.language as NarrationLang;
    if (!langs.includes(lang)) continue;
    if (item.status === "missing") {
      out.toGenerate.push(item);
      out.perLang[lang] += 1;
      out.chars += item.chars;
    } else {
      out[item.status] += 1;
    }
  }
  return out;
}

export interface NarrationPlanDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Exhibits or zones (F-W) — picks the plan endpoint. */
  kind: NarrationKind;
  ids: string[];
}

const NarrationPlanDialog: FC<NarrationPlanDialogProps> = ({
  open,
  onOpenChange,
  kind,
  ids,
}) => {
  const { start, isRunning } = useNarrationRun();
  const [langs, setLangs] = useState<NarrationLang[]>([...NARRATION_LANGS]);
  const {
    data: plan,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["narration-plan", kind, ids],
    queryFn: () => planNarration(kind, ids),
    enabled: open,
    staleTime: 0,
    gcTime: 0,
  });

  const summary = useMemo(
    () => (plan ? summarizePlan(plan, langs) : null),
    [plan, langs],
  );
  const remaining = plan
    ? Math.max(plan.budget.budgetChars - plan.budget.usedChars, 0)
    : 0;
  const disabledReason = !plan
    ? null
    : !plan.budget.generationEnabled
      ? "AI narration is switched off by an admin"
      : isRunning
        ? "A narration run is in progress"
        : summary && summary.chars > remaining
          ? `Needs ${fmt(summary.chars)} characters, but only ${fmt(remaining)} are left this month. Untick a language or ask an admin to add budget.`
          : null;
  const count = summary?.toGenerate.length ?? 0;

  const toggle = (lang: NarrationLang) =>
    setLangs((prev) =>
      prev.includes(lang) ? prev.filter((l) => l !== lang) : [...prev, lang],
    );

  const generate = () => {
    if (!summary) return;
    start(
      summary.toGenerate.map((i) => ({
        kind,
        ownerId: i.ownerId,
        code: i.code,
        titleTc: i.titleTc,
        language: i.language,
        chars: i.chars,
      })),
    );
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="narration-plan-dialog">
        <DialogHeader>
          <DialogTitle>Generate Narration Audio</DialogTitle>
          <DialogDescription>
            Only exhibits without narration audio in a language are generated.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-4 text-sm">
          <span className="text-muted-foreground">Languages</span>
          {NARRATION_LANGS.map((lang) => (
            <label key={lang} className="flex items-center gap-1">
              <input
                type="checkbox"
                checked={langs.includes(lang)}
                onChange={() => toggle(lang)}
                data-testid={`narration-plan-lang-${lang}`}
              />
              {LANG_LABEL[lang]}
            </label>
          ))}
        </div>

        {isLoading && (
          <p className="text-sm text-muted-foreground">Checking exhibits…</p>
        )}
        {isError && (
          <p className="text-sm text-red-600">Could not load the plan.</p>
        )}
        {summary && plan && (
          <div
            className="space-y-3 text-sm"
            data-testid="narration-plan-summary"
          >
            {count === 0 ? (
              <p className="font-medium" data-testid="narration-plan-nothing">
                Nothing to generate — every selected language already has
                narration audio or has no description.
              </p>
            ) : (
              <div>
                <p className="font-medium" data-testid="narration-plan-count">
                  {count} narration audio will be generated
                </p>
                <p className="text-muted-foreground">
                  {NARRATION_LANGS.filter((l) => langs.includes(l))
                    .map((l) => `${LANG_LABEL[l]} ${summary.perLang[l]}`)
                    .join(" · ")}
                </p>
              </div>
            )}
            <div data-testid="narration-plan-skipped">
              <p className="text-muted-foreground">Skipped</p>
              <ul className="ml-4">
                <li>{summary.current} already have narration audio</li>
                <li>{summary.custom} custom audio (never replaced)</li>
                <li>{summary.none} no description in that language</li>
                <li>
                  {summary.stale} outdated — regenerate these one by one on the
                  exhibit&apos;s Narration tab
                </li>
              </ul>
            </div>
            {count > 0 && (
              <p data-testid="narration-plan-chars">
                Uses {fmt(summary.chars)} character{plural(summary.chars)}
                <span className="block text-muted-foreground">
                  {fmt(remaining)} of {fmt(plan.budget.budgetChars)} left this
                  month
                </span>
              </p>
            )}
            {disabledReason && count > 0 && (
              <p className="text-red-600" data-testid="narration-plan-blocked">
                {disabledReason}
              </p>
            )}
            {count > 0 && !disabledReason && (
              <p className="text-xs text-muted-foreground">
                ⓘ Keep this page open until audio generation is complete.
              </p>
            )}
          </div>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="narration-plan-cancel"
          >
            {count === 0 ? "Close" : "Cancel"}
          </Button>
          {count > 0 && (
            <Button
              type="button"
              onClick={generate}
              disabled={Boolean(disabledReason)}
              data-testid="narration-plan-generate"
            >
              Generate {count}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default NarrationPlanDialog;
